import React, { useEffect, useState } from 'react';
import {
  connect,
  MapDispatchToProps,
  MapStateToProps,
  useSelector,
} from 'react-redux';
import Select from 'components/Select';
import { getTags } from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/GeneralQuestionEditor';
import Button from 'components/Button/Button';
import notify from 'components/Notify';
import {
  createPDPPanel,
  createPDPPanelFn,
  updatePDPConfig,
  updatePDPConfigFn,
} from 'store/pdp/pdpSlice';
import { notifyError } from 'components/Notify/Notify';
import { RouteComponentProps } from '@reach/router';
import {
  BasicConfigChild,
  BasicConfigContainer,
  ButtonGroupContainer,
  ConfigContainer,
  Container,
  SelectedButtonGroupContent,
} from './style';
import cloneDeep from 'lodash/cloneDeep';
import {
  requireProviderDocuments,
  requireProviderDocumentsFn,
  requireProviderTags,
  requireProviderTagsFn,
} from 'store/providers/providerSlice';
import TaskFilePicker from '../../TasksManagement/TaskFilePicker';
import { ClassPhaseEditorClass } from '../PDPClass/ClassPhaseEditorClass';
import { appDispatch, RootState } from 'store/store';
import {
  ClassPhaseProp,
  CompanyPDP,
  EffectivityFormula,
  ICompany,
  ICompanyProviders,
  ICompanyRole,
  PdpOutputConfig,
  PDPOutputFilter,
} from 'store/companies/companiesTypes';
import {
  IProviders,
  IRelatedProviderItem,
  ITags,
  ITagView,
} from 'store/providers/providerTypes';
import ButtonGroup from '../../../../components/ButtonGroup';
import i18n from '../../../../i18n';
import Popover from '../../../../components/Popover/Popover';
import { black, exactWhite, lightGray } from '../../../../utils/style/colors';
import SelectWithPopover, {
  IOption,
} from '../../../../components/Select/SelectWithPopover';
import Input from '../../../../components/Input/Input';
import LocalIcon from '../../../../ui/LocalIcon';
import Collapsible from '../../../../components/Collapsible';
import Textarea from '../../../../components/Textarea';
import Icon from '../../../../components/Icon';

const pdpOptions: IOption[] = [
  { label: i18n.t('hat', { ns: 'companyManagement' }), value: 'teacher.svg' },
  {
    label: i18n.t('openBook', { ns: 'companyManagement' }),
    value: 'open-book.svg',
  },
  {
    label: i18n.t('closedBook', { ns: 'companyManagement' }),
    value: 'closed-book.svg',
  },
];

interface IOwnProps {
  companyId?: string;
}
interface StateToProps {
  company: ICompany;
  tags: ITags['data'];
  toLoadTags: number[];
  relatedProviders: ICompanyProviders;
  providers: IProviders;
}
interface DispatchToProps {
  updatePDPConfig: (
    pdpConfig: CompanyPDP
  ) => ReturnType<typeof updatePDPConfigFn>;
  createPDPPanel: (
    companyId: number,
    templateName: string
  ) => ReturnType<typeof createPDPPanelFn>;
  requireProviderTags: (
    providerId: number
  ) => ReturnType<typeof requireProviderTagsFn>;
  requireProviderItems: (
    itemIds: number[]
  ) => ReturnType<typeof requireProviderDocumentsFn>;
}
type ComponentProps = IOwnProps &
  StateToProps &
  DispatchToProps &
  RouteComponentProps;

const PdpConfig: React.FC<ComponentProps> = ({
  company,
  tags: storeTags,
  updatePDPConfig,
  createPDPPanel,
  toLoadTags,
  requireProviderTags,
  providers,
  relatedProviders,
  requireProviderItems,
}) => {
  const pdpTemplateOptions: IOption[] = [
    {
      label: i18n.t('newEmployee', { ns: 'companyManagement' }),
      value: 'newEmployee',
    },
    {
      label: i18n.t('pdpDevelopment', { ns: 'companyManagement' }),
      value: 'development',
    },
  ];
  const EffectivityFormulaOptions: IOption[] = [
    {
      label: i18n.t('EffectivityFormula_Advanced', { ns: 'companyManagement' }),
      value: 'Advanced',
    },
    {
      label: i18n.t('EffectivityFormula_Simple', { ns: 'companyManagement' }),
      value: 'Simple',
    },
  ];

  const users = useSelector((state: RootState) => state.users.map);

  const [tags, setTags] = useState(storeTags);
  const [topicTags, setTopicTags] = useState<Array<number>>([]);
  const [topicTagsInPDP, setTopicTagsInPDP] = useState<Array<number>>([]);
  const [selectedCompanyPdp, setSelectedCompanyPdp] =
    useState<CompanyPDP | null>(null);
  const [selectedNewPdp, setSelectedNewPdp] = useState<string>();
  const [classHomeFolder, setClassHomeFolder] = useState<number>();
  const [classPhaseParentTag, setClassPhaseParentTag] = useState<number>();
  const [classHoldTypes, setClassHoldTypes] = useState<number>();
  const [classLocations, setClassLocations] = useState<number>();
  const [effectivityFormula, setEffectivityFormula] =
    useState<EffectivityFormula>();
  const [classPhaseDefaultProps, setClassPhaseDefaultProps] = useState<{
    [key: number]: ClassPhaseProp;
  }>();
  const [managerRole, setManagerRole] = useState<number>();
  const [selectedCompanyPdpName, setSelectedCompanyPdpName] =
    useState<string>();
  const [selectedCompanyPdpIcon, setSelectedCompanyPdpIcon] =
    useState<string>();
  const [saving, setSaving] = useState(false);
  const [conflictCheckConfig, setConflictCheckConfig] = useState<{
    enabled: 'Off' | 'Warning' | 'Error';
    checkForHoldTypes: number[];
    checkForClassLocations: number[];
    checkForPhases: number[];
    allowOverlapMinutes: number;
  }>({
    enabled: 'Off',
    checkForHoldTypes: [],
    checkForClassLocations: [],
    checkForPhases: [],
    allowOverlapMinutes: 0,
  });
  const [outputConfigs, setOutputConfigs] = useState<PdpOutputConfig[]>([]);

  const save = () => {
    if (selectedCompanyPdp) {
      const result = cloneDeep(selectedCompanyPdp);
      result.properties.name = selectedCompanyPdpName;
      result.properties.icon = selectedCompanyPdpIcon;
      result.properties.topicTags = topicTags;
      result.properties.topicTagsInPDP = topicTagsInPDP;
      result.properties.classPhaseParentTag = classPhaseParentTag;
      result.properties.classHoldType = classHoldTypes;
      result.properties.classLocations = classLocations;
      result.properties.effectivityFormula = effectivityFormula;
      result.properties.classHomeFolder = classHomeFolder;
      result.properties.classPhaseDefaultProps = Object.values(
        classPhaseDefaultProps || {}
      );
      result.properties.checkClassTimeConflict = conflictCheckConfig;
      result.properties.outputConfigs = outputConfigs;
      result.managerRoleId = managerRole;
      setSaving(true);
      updatePDPConfig(result)
        .then(
          () => {
            notify(i18n.t('pdpPanelSaved', { ns: 'companyManagement' }));
          },
          (reason: any) => {
            notifyError(reason);
          }
        )
        .finally(() => {
          setSaving(false);
        });
    }
  };
  const createPanel = () => {
    createPDPPanel(company.id, selectedNewPdp || '').then(() => {
      notify(i18n.t('pdpPanelCreated', { ns: 'companyManagement' }));
      setSelectedNewPdp(undefined);
      // window.location.reload();
    });
  };

  const allRoles = Object.values(company.roles) as ICompanyRole[];
  const allRoleOptions =
    allRoles
      ?.filter((it) => !it.isProjectRole)
      ?.map((it) => ({ value: it.id, label: it.displayName! })) || [];

  const relatedProviderItems: IRelatedProviderItem[] = [];
  if (classHomeFolder) {
    Object.values(relatedProviders).forEach((relatedProvider) => {
      const providerId = relatedProvider!.id;
      const found = providers.items[providerId].items[classHomeFolder];
      if (found)
        relatedProviderItems.push({
          providerId,
          id: classHomeFolder,
          type: 'folder',
          title: found.data.name!,
        });
      // else requireProviderItems([classHomeFolder]);
    });
  }

  useEffect(() => {
    if (classHomeFolder) requireProviderItems([classHomeFolder]);
  }, [classHomeFolder]);

  const childTags = (
    Object.values(
      Object.values(tags).filter((tag) => {
        return tag.parentId === classPhaseParentTag && !!classPhaseParentTag;
      })
    ) as ITagView[]
  ).sort((a, b) => (a.order ?? Number(a.id)) - (b.order ?? Number(b.id)));

  const enabledTags: number[] = [];
  for (const [tagPhaseId, tag] of Object.entries(
    classPhaseDefaultProps || {}
  )) {
    if (tag.disabled) enabledTags.push(+tagPhaseId);
  }
  const tagStepsButtonGroupOptions = childTags.reduce((acc, currentTag) => {
    acc[`P${currentTag.id}`] = (
      <div style={{ textAlign: 'right' }}>
        {` ${!enabledTags.includes(+currentTag.id) ? '✅' : ''} ${currentTag.text}`}
      </div>
    );
    return acc;
  }, {});
  const [selectedTag, setSelectedTag] = useState(
    `P${childTags?.[0]?.id}` || ''
  );

  useEffect(() => {
    toLoadTags.forEach((it) => {
      requireProviderTags(it);
    });
  }, []);

  useEffect(() => {
    setClassHomeFolder(
      selectedCompanyPdp?.properties.classHomeFolder as number
    );
    setClassPhaseParentTag(selectedCompanyPdp?.properties.classPhaseParentTag);
    setClassHoldTypes(selectedCompanyPdp?.properties.classHoldType);
    setClassLocations(selectedCompanyPdp?.properties.classLocations);
    setEffectivityFormula(selectedCompanyPdp?.properties.effectivityFormula);
    setClassPhaseDefaultProps(
      selectedCompanyPdp?.properties?.classPhaseDefaultProps?.reduce(
        (current, item) => {
          current[item.phaseTagId] = item;
          return current;
        },
        {} as { [key: number]: ClassPhaseProp }
      ) ?? {}
    );
    setManagerRole(selectedCompanyPdp?.managerRoleId);
    setSelectedCompanyPdpName(
      selectedCompanyPdp?.properties?.name ||
        i18n.t('pdp', { ns: 'companyManagement' })
    );
    setSelectedCompanyPdpIcon(selectedCompanyPdp?.properties.icon);
    setConflictCheckConfig(
      selectedCompanyPdp?.properties.checkClassTimeConflict || {
        enabled: 'Off',
        checkForHoldTypes: [],
        checkForClassLocations: [],
        checkForPhases: [],
        allowOverlapMinutes: 0,
      }
    );
    setOutputConfigs(selectedCompanyPdp?.properties.outputConfigs || []);
    if (selectedCompanyPdp) {
      setTopicTags(selectedCompanyPdp.properties.topicTags || []);
      setTopicTagsInPDP(selectedCompanyPdp.properties.topicTagsInPDP || []);
    }
  }, [selectedCompanyPdp]);

  useEffect(() => {
    if (!storeTags || Object.keys(storeTags).length === 0) return;
    setTags(storeTags);
    if (selectedCompanyPdp) {
      setTopicTags(selectedCompanyPdp.properties.topicTags || []);
      setSelectedTag(`P${childTags?.[0]?.id}` || '');
      setTopicTagsInPDP(selectedCompanyPdp.properties.topicTagsInPDP || []);
    }
  }, [storeTags]);

  const handleSelectedTag = (value: string) => {
    setSelectedTag(value);
  };

  return (
    <div className="flex gap-2 flex-1">
      <div
        style={{
          width: '15%',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <ButtonGroup
          items={
            company.pdpPanels?.reduce(
              (acc, it) => ({
                ...acc,
                [it.id.toString()]:
                  it.properties?.name ||
                  i18n.t('pdp', { ns: 'companyManagement' }),
              }),
              {}
            ) ?? {}
          }
          onSelect={(value) => {
            setSelectedCompanyPdp(
              company.pdpPanels.find((it) => it.id === +value)!
            );
          }}
          selected={selectedCompanyPdp?.id.toString() || ''}
          vertical={true}
        />
        <Popover
          trigger={'click'}
          overlay={
            <div style={{ width: '15rem', background: exactWhite }}>
              <Select
                onChangeValues={(values) =>
                  setSelectedNewPdp(values[0].value.toString())
                }
                options={pdpTemplateOptions}
                values={pdpTemplateOptions.filter(
                  (it) => it.value === selectedNewPdp
                )}
                multiChoice={false}
              />
              <Button loading={saving} onClick={createPanel}>
                {i18n.t('create', { ns: 'companyManagement' }) as string}
              </Button>
            </div>
          }
        >
          <Button>
            {i18n.t('createPDPPanel', { ns: 'companyManagement' }) as string}
          </Button>
        </Popover>
      </div>
      {selectedCompanyPdp && (
        <div style={{ overflow: 'hidden', flex: '1', height: '100%' }}>
          <Container>
            <BasicConfigContainer>
              <BasicConfigChild>
                <Input
                  id={`pdp-name-${selectedCompanyPdp.id}`}
                  type="text"
                  label={
                    i18n.t('pdpName', { ns: 'companyManagement' }) as string
                  }
                  value={selectedCompanyPdpName}
                  onChange={(event: any) =>
                    setSelectedCompanyPdpName(event.target.value)
                  }
                  wrapperStyle={{ width: '50%' }}
                />
                <Select
                  label={
                    i18n.t('pdpIcon', { ns: 'companyManagement' }) as string
                  }
                  onChangeValues={(values) =>
                    setSelectedCompanyPdpIcon(
                      values?.[0]?.value.toString() || ''
                    )
                  }
                  options={pdpOptions}
                  values={pdpOptions.filter(
                    (it) => it.value === selectedCompanyPdpIcon
                  )}
                  containerStyle={{ width: '50%' }}
                />
                <LocalIcon
                  iconName={selectedCompanyPdpIcon as any}
                  color={black}
                />
              </BasicConfigChild>
              <BasicConfigChild>
                <Select
                  multiChoice={true}
                  options={getTags(tags || {}, false)}
                  values={topicTags?.map((it) => ({
                    label: tags[it.toString()]?.text || '',
                    value: it,
                  }))}
                  onChangeValues={(values) => {
                    const value = values.map((it) => Number(it?.value));
                    setTopicTags(value);
                  }}
                  label={
                    i18n.t('topicTags', { ns: 'companyManagement' }) as string
                  }
                  containerStyle={{ width: '100%' }}
                />
                <Select
                  multiChoice={true}
                  options={getTags(tags || {}, false)}
                  values={topicTagsInPDP?.map((it) => ({
                    label: tags[it.toString()]?.text || '',
                    value: it,
                  }))}
                  onChangeValues={(values) => {
                    const value = values.map((it) => Number(it?.value));
                    setTopicTagsInPDP(value);
                  }}
                  label={
                    i18n.t('topicTagsInPDP', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />
              </BasicConfigChild>

              <BasicConfigChild>
                <Select
                  label={
                    i18n.t('pdpAdminRole', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  values={allRoleOptions.filter(
                    (it) => managerRole === Number(it.value)
                  )}
                  options={allRoleOptions}
                  multiChoice={false}
                  containerStyle={{ width: '50%' }}
                  onChangeValues={(selected) => {
                    setManagerRole(Number(selected[0]?.value) || undefined);
                  }}
                />

                <TaskFilePicker
                  editable={true}
                  items={relatedProviderItems}
                  onRemove={() => () => {
                    setClassHomeFolder(undefined);
                  }}
                  onSave={(it: IRelatedProviderItem) =>
                    setClassHomeFolder(it.id)
                  }
                  providers={relatedProviders}
                  title={i18n.t('pdpHomeFolder', { ns: 'companyManagement' })}
                  task={undefined}
                />
              </BasicConfigChild>

              <BasicConfigChild>
                <Select
                  multiChoice={false}
                  allowRemove={false}
                  options={getTags(tags || {}, false)}
                  values={
                    classPhaseParentTag
                      ? [classPhaseParentTag].map((it) => {
                          return {
                            label: tags[it.toString()]?.text || '',
                            value: it,
                          };
                        })
                      : []
                  }
                  onChangeValues={(values) => {
                    if (!values.length) setClassPhaseParentTag(undefined);
                    else {
                      const value = Number(values[0].value);
                      setClassPhaseParentTag(value);
                    }
                  }}
                  label={
                    i18n.t('classParentTag', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  containerStyle={{ width: '30%' }}
                />
                <Select
                  multiChoice={false}
                  allowRemove={false}
                  options={getTags(tags || {}, false)}
                  values={
                    classHoldTypes
                      ? [classHoldTypes].map((it) => {
                          return {
                            label: tags[it.toString()]?.text || '',
                            value: it,
                          };
                        })
                      : []
                  }
                  onChangeValues={(values) => {
                    if (!values.length) setClassHoldTypes(undefined);
                    else {
                      const value = Number(values[0].value);
                      setClassHoldTypes(value);
                    }
                  }}
                  label={
                    i18n.t('classHoldType', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  containerStyle={{ width: '30%' }}
                />
                <Select
                  multiChoice={false}
                  allowRemove={false}
                  options={getTags(tags || {}, false)}
                  values={
                    classLocations
                      ? [classLocations].map((it) => {
                          return {
                            label: tags[it.toString()]?.text || '',
                            value: it,
                          };
                        })
                      : []
                  }
                  onChangeValues={(values) => {
                    if (!values.length) setClassHoldTypes(undefined);
                    else {
                      const value = Number(values[0].value);
                      setClassLocations(value);
                    }
                  }}
                  label={
                    i18n.t('classLocations', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  containerStyle={{ width: '30%' }}
                />
                <Select
                  onChangeValues={(values) =>
                    setEffectivityFormula(
                      values[0].value.toString() as EffectivityFormula
                    )
                  }
                  label={
                    i18n.t('effectiveFormula', {
                      ns: 'companyManagement',
                    }) as string
                  }
                  options={EffectivityFormulaOptions}
                  values={EffectivityFormulaOptions.filter(
                    (it) => it.value === effectivityFormula
                  )}
                  allowRemove={false}
                  multiChoice={false}
                />
              </BasicConfigChild>

              <BasicConfigChild>
                <Collapsible
                  header={`${i18n.t('classTimeConflictCheck', { ns: 'companyManagement' })}: ${i18n.t(conflictCheckConfig.enabled, { ns: 'companyManagement' })}`}
                  defaultOpen={false}
                  style={{ width: '100%' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <Select
                      label={
                        i18n.t('enabled', { ns: 'companyManagement' }) as string
                      }
                      options={[
                        {
                          label: i18n.t('Off', { ns: 'companyManagement' }),
                          value: 'Off',
                        },
                        {
                          label: i18n.t('Warning', { ns: 'companyManagement' }),
                          value: 'Warning',
                        },
                        {
                          label: i18n.t('Error', { ns: 'companyManagement' }),
                          value: 'Error',
                        },
                      ]}
                      values={[
                        {
                          label: i18n.t(conflictCheckConfig.enabled, {
                            ns: 'companyManagement',
                          }),
                          value: conflictCheckConfig.enabled,
                        },
                      ]}
                      onChangeValues={(values) => {
                        setConflictCheckConfig({
                          ...conflictCheckConfig,
                          enabled: values[0]?.value as
                            | 'Off'
                            | 'Warning'
                            | 'Error',
                        });
                      }}
                      multiChoice={false}
                      containerStyle={{ width: '30%' }}
                    />
                    <Input
                      id="allowOverlapMinutes"
                      type="number"
                      label={
                        i18n.t('allowOverlapMinutes', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      value={conflictCheckConfig.allowOverlapMinutes.toString()}
                      onChange={(event: any) => {
                        setConflictCheckConfig({
                          ...conflictCheckConfig,
                          allowOverlapMinutes: Number(event.target.value) || 0,
                        });
                      }}
                      wrapperStyle={{ width: '30%' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <SelectWithPopover
                      label={
                        i18n.t('checkForHoldTypes', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      multiChoice={true}
                      options={
                        classHoldTypes
                          ? Object.values(tags)
                              .filter((tag) => tag.parentId === classHoldTypes)
                              .map((tag) => ({
                                label: tag.text,
                                value: tag.id,
                              }))
                          : []
                      }
                      values={conflictCheckConfig.checkForHoldTypes.map(
                        (id) => ({
                          label: tags[id.toString()]?.text || '',
                          value: id,
                        })
                      )}
                      onChangeValues={(values) => {
                        setConflictCheckConfig({
                          ...conflictCheckConfig,
                          checkForHoldTypes: values.map((v) => Number(v.value)),
                        });
                      }}
                      containerStyle={{ width: '33%' }}
                    />
                    <SelectWithPopover
                      label={
                        i18n.t('checkForClassLocations', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      multiChoice={true}
                      options={
                        classLocations
                          ? Object.values(tags)
                              .filter((tag) => tag.parentId === classLocations)
                              .map((tag) => ({
                                label: tag.text,
                                value: tag.id,
                              }))
                          : []
                      }
                      values={conflictCheckConfig.checkForClassLocations.map(
                        (id) => ({
                          label: tags[id.toString()]?.text || '',
                          value: id,
                        })
                      )}
                      onChangeValues={(values) => {
                        setConflictCheckConfig({
                          ...conflictCheckConfig,
                          checkForClassLocations: values.map((v) =>
                            Number(v.value)
                          ),
                        });
                      }}
                      containerStyle={{ width: '33%' }}
                    />
                    <SelectWithPopover
                      label={
                        i18n.t('checkForPhases', {
                          ns: 'companyManagement',
                        }) as string
                      }
                      multiChoice={true}
                      options={
                        classPhaseParentTag
                          ? Object.values(tags)
                              .filter(
                                (tag) => tag.parentId === classPhaseParentTag
                              )
                              .map((tag) => ({
                                label: tag.text,
                                value: tag.id,
                              }))
                          : []
                      }
                      values={conflictCheckConfig.checkForPhases.map((id) => ({
                        label: tags[id.toString()]?.text || '',
                        value: id,
                      }))}
                      onChangeValues={(values) => {
                        setConflictCheckConfig({
                          ...conflictCheckConfig,
                          checkForPhases: values.map((v) => Number(v.value)),
                        });
                      }}
                      containerStyle={{ width: '33%' }}
                    />
                  </div>
                </Collapsible>
              </BasicConfigChild>
            </BasicConfigContainer>
            <BasicConfigContainer>
              <Collapsible
                header={i18n.t('outputConfigs', { ns: 'companyManagement' })}
                defaultOpen={false}
                style={{ width: '100%' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                  }}
                >
                  {outputConfigs.map((config, index) => {
                    const fieldOptions = [
                      {
                        label: i18n.t('title', { ns: 'companyManagement' }),
                        value: 'title',
                      },
                      {
                        label: i18n.t('period', { ns: 'companyManagement' }),
                        value: 'period',
                      },
                      {
                        label: i18n.t('holdType', { ns: 'companyManagement' }),
                        value: 'holdType',
                      },
                      {
                        label: i18n.t('trainingHour', {
                          ns: 'companyManagement',
                        }),
                        value: 'trainingHour',
                      },
                      {
                        label: i18n.t('presence', {
                          ns: 'companyManagement',
                        }),
                        value: 'presence',
                      },
                      {
                        label: i18n.t('status', {
                          ns: 'companyManagement',
                        }),
                        value: 'status',
                      },
                      {
                        label: i18n.t('status', {
                          ns: 'companyManagement',
                        }),
                        value: 'status',
                      },
                      {
                        label: i18n.t('effectivity', {
                          ns: 'companyManagement',
                        }),
                        value: 'effectivity',
                      },
                      ...childTags.map((tag) => ({
                        label: `${i18n.t('score', { ns: 'companyManagement' })}: ${tag.text}`,
                        value: `p_${tag.id}`,
                      })),
                      ...topicTags.map((tagId) => ({
                        label: `${i18n.t('topicTag', { ns: 'companyManagement' })}: ${tags[tagId.toString()]?.text || ''}`,
                        value: `t_${tagId}`,
                      })),
                    ];
                    const groupOptions = topicTags.map((tagId) => ({
                      label: tags[tagId.toString()]?.text || '',
                      value: tagId,
                    }));
                    const filterOptions: IOption[] = [
                      {
                        label: i18n.t('onlyApproved', {
                          ns: 'companyManagement',
                        }),
                        value: 'OnlyApproved',
                      },
                      {
                        label: i18n.t('onlyPresent', {
                          ns: 'companyManagement',
                        }),
                        value: 'OnlyPresent',
                      },
                      {
                        label: i18n.t('onlyWithClass', {
                          ns: 'companyManagement',
                        }),
                        value: 'OnlyWithClass',
                      },
                    ];
                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          gap: '1rem',
                          padding: '1rem',
                          border: `1px solid ${lightGray}`,
                          borderRadius: '1rem',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Input
                          id={`config-name-${index}`}
                          type="text"
                          label={
                            i18n.t('configName', {
                              ns: 'companyManagement',
                            }) as string
                          }
                          value={config.name}
                          onChange={(e: any) => {
                            const newConfigs = [...outputConfigs];
                            newConfigs[index] = {
                              ...config,
                              name: e.target.value,
                            };
                            setOutputConfigs(newConfigs);
                          }}
                          wrapperStyle={{ flex: '1' }}
                        />
                        <Select
                          label={
                            i18n.t('fields', {
                              ns: 'companyManagement',
                            }) as string
                          }
                          multiChoice={true}
                          options={fieldOptions}
                          values={config.fields.map((field) => ({
                            label:
                              fieldOptions.find((opt) => opt.value === field)
                                ?.label || field,
                            value: field,
                          }))}
                          onChangeValues={(values) => {
                            const newConfigs = [...outputConfigs];
                            newConfigs[index] = {
                              ...config,
                              fields: values.map((v) => v.value.toString()),
                            };
                            setOutputConfigs(newConfigs);
                          }}
                          containerStyle={{ flex: '1' }}
                        />
                        <Select
                          label={
                            i18n.t('group', {
                              ns: 'companyManagement',
                            }) as string
                          }
                          multiChoice={false}
                          allowRemove={true}
                          options={groupOptions}
                          values={
                            config.group
                              ? [
                                  {
                                    label:
                                      tags[config.group.toString()]?.text || '',
                                    value: config.group,
                                  },
                                ]
                              : []
                          }
                          onChangeValues={(values) => {
                            const newConfigs = [...outputConfigs];
                            newConfigs[index] = {
                              ...config,
                              group:
                                values.length > 0
                                  ? Number(values[0].value)
                                  : null,
                            };
                            setOutputConfigs(newConfigs);
                          }}
                          containerStyle={{ flex: '1' }}
                        />
                        <Select
                          label={
                            i18n.t('filters', {
                              ns: 'companyManagement',
                            }) as string
                          }
                          multiChoice={true}
                          options={filterOptions}
                          values={config.filters.map((filter) => ({
                            label:
                              filterOptions.find((opt) => opt.value === filter)
                                ?.label || filter,
                            value: filter,
                          }))}
                          onChangeValues={(values) => {
                            const newConfigs = [...outputConfigs];
                            newConfigs[index] = {
                              ...config,
                              filters: values.map(
                                (v) => v.value as PDPOutputFilter
                              ),
                            };
                            setOutputConfigs(newConfigs);
                          }}
                          containerStyle={{ flex: '1' }}
                        />
                        <Textarea
                          id={`config-bottom-text-${index}`}
                          label={
                            i18n.t('bottomText', {
                              ns: 'companyManagement',
                            }) as string
                          }
                          value={config.bottomText}
                          onChange={(e: any) => {
                            const newConfigs = [...outputConfigs];
                            newConfigs[index] = {
                              ...config,
                              bottomText: e.target.value,
                            };
                            setOutputConfigs(newConfigs);
                          }}
                          wrapperStyle={{ flex: '1' }}
                        />
                        <Button
                          onClick={() => {
                            const newConfigs = outputConfigs.filter(
                              (_, i) => i !== index
                            );
                            setOutputConfigs(newConfigs);
                          }}
                          color="red"
                        >
                          <Icon type="trash" />
                        </Button>
                      </div>
                    );
                  })}
                  <Button
                    onClick={() => {
                      setOutputConfigs([
                        ...outputConfigs,
                        {
                          name: '',
                          fields: [],
                          group: null,
                          filters: [],
                          bottomText: '',
                        },
                      ]);
                    }}
                  >
                    {
                      i18n.t('addOutputConfig', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </Button>
                </div>
              </Collapsible>
            </BasicConfigContainer>

            <ConfigContainer>
              {Object.keys(tagStepsButtonGroupOptions).length ? (
                <ButtonGroupContainer>
                  <ButtonGroup
                    items={tagStepsButtonGroupOptions}
                    onSelect={handleSelectedTag}
                    selected={selectedTag}
                    vertical={true}
                  />
                </ButtonGroupContainer>
              ) : (
                <></>
              )}
              <SelectedButtonGroupContent>
                {childTags.map((tag: ITagView) => {
                  const config = classPhaseDefaultProps?.[tag.id];
                  return selectedTag === `P${tag.id}` ? (
                    <React.Fragment key={tag.id}>
                      <ClassPhaseEditorClass
                        phaseInfo={config || { phaseTagId: tag.id }}
                        updatePhase={(phaseInfo) => {
                          setClassPhaseDefaultProps({
                            ...classPhaseDefaultProps,
                            [tag.id.toString()]: phaseInfo,
                          });
                        }}
                        company={company}
                        clazz={undefined}
                        topic={undefined}
                        tag={tag}
                        isTemplate={true}
                        classPhases={childTags}
                        relatedProviders={relatedProviders}
                        updateSurvey={undefined}
                        isOpen={true}
                        users={users}
                        pdpId={selectedCompanyPdp.id}
                      />
                      {/*<Card style={{ flex: ' 0 0 560px' }} key={tag.id}>
                  <div>{tag.text}</div>
                  <Select
                    label={messages.documentVisibility}
                    onChangeValues={(values) => {
                      const newConf = {
                        ...config,
                        contentVisible: values[0].value,
                        phaseTagId: tag.id,
                        phaseStartDate: 0,
                        phaseEndDate: 0,
                      };
                      setClassPhaseDefaultProps({
                        ...classPhaseDefaultProps,
                        [tag.id.toString()]: newConf,
                      });
                    }}
                    options={visibilityOptions}
                    values={visibilityOptions.filter(
                      (it) =>
                        it.value === (config?.contentVisible || 'DuringPhase')
                    )}
                  />
                  <PhaseReminder
                    phaseProps={
                      config || { reminders: [], phaseTagId: tag.id.toString() }
                    }
                    updatePhase={(it) => {
                      setClassPhaseDefaultProps({
                        ...classPhaseDefaultProps,
                        [tag.id.toString()]: it,
                      });
                    }}
                  />
                </Card>*/}
                    </React.Fragment>
                  ) : null;
                })}
              </SelectedButtonGroupContent>
            </ConfigContainer>
            <Button
              style={{ margin: '0.5rem 0' }}
              onClick={save}
              loading={saving}
              color={'green'}
            >
              {i18n.t('save', { ns: 'companyManagement' }) as string}
            </Button>
          </Container>
        </div>
      )}
    </div>
  );
};

const mapStateToProps: MapStateToProps<StateToProps, IOwnProps, RootState> = (
  state,
  ownProps
) => {
  const providers = state.providers;
  const providerIds = Object.values(providers.map).reduce(
    (current: number[], it: any) => {
      if (it.data.managingCompanyId === Number(ownProps.companyId))
        current.push(it.data.id);
      return current;
    },
    [] as number[]
  );
  let tags = {};
  const toLoadTags = [] as number[];
  providerIds.forEach((id: number) => {
    if (!state.providers.tags[id]?.status) toLoadTags.push(id);
    tags = {
      ...tags,
      ...(state.providers.tags[id]?.data || {}),
    };
  });
  const relatedProviders =
    state.companies.data[Number(ownProps.companyId)].relatedProviders;
  return {
    toLoadTags,
    company: state.companies.data[Number(ownProps.companyId)],
    tags: tags,
    relatedProviders,
    providers,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  DispatchToProps,
  IOwnProps
> = () => {
  return {
    updatePDPConfig: (pdpConfig: CompanyPDP) =>
      appDispatch(updatePDPConfig({ pdpConfig })),
    createPDPPanel: (companyId: number, templateName: string) =>
      appDispatch(createPDPPanel({ companyId, templateName })),
    requireProviderTags: (providerId: number) =>
      appDispatch(requireProviderTags({ providerId })),
    requireProviderItems: (documents: number[]) =>
      appDispatch(requireProviderDocuments({ documentIds: documents })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PdpConfig);
